import { authHeadersFor, getImpersonation, type ImpersonationTarget } from '../../core/impersonation';
import { getJWTToken, parseJwt } from '../../utils/auth';
import { loadListState, resetListState } from './listState';
import { parseRosterMeta, type RosterMeta } from '../../core/rosterMeta';
import { NO_SCHOOL } from './listRows';

const apiUrl = import.meta.env.REACT_APP_BAMBOO_API_URL ?? '';

export type WindowPhase = 'open' | 'closed' | 'mixed' | 'undated';

export type ProgressReportWindow = {
  window: string;
  schoolYear: number;
  label: string;
  short: string;
  phase?: WindowPhase;
};

export type BenchmarkState = 'current' | 'due' | 'to_score' | 'missed' | 'none';

export type ProgressReportStudent = {
  studentId: string;
  firstName: string;
  lastName: string;
  school: string;
  grade: string;
  teacher: string;
  teachers: string[];
  class?: string;
  classes?: string[];
  sisClass?: string;
  latestBenchmark: ProgressReportWindow | null;
  inCurrentWindow: boolean;
  state?: BenchmarkState;
  source?: string;
  inFlashlight?: boolean;
};

export type ProgressReportStudents = {
  currentWindow: ProgressReportWindow | null;
  students: ProgressReportStudent[];
  roster?: RosterMeta | null;
  schools?: string[];
  school?: string;
};

export type ListFetch = { school: string | null };

export function schoolLabel(raw: string): string {
  return raw.trim() === '' ? NO_SCHOOL : raw;
}

function schoolQuery(query: ListFetch | undefined): string {
  if (query === undefined) return '';
  if (query.school === null) return '?pick=first';
  return `?school=${encodeURIComponent(query.school === NO_SCHOOL ? '' : query.school)}`;
}

function cacheKey(query: ListFetch | undefined): string {
  if (query === undefined) return '*';
  return query.school === null ? '*first' : `school:${query.school}`;
}

export class ProgressReportStudentsError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ProgressReportStudentsError';
    this.status = status;
  }
}

export type AuthSnapshot = { owner: string; token: string | undefined; target: ImpersonationTarget | null };

export async function authSnapshot(): Promise<AuthSnapshot> {
  let sub = '';
  let token: string | undefined;
  try {
    token = await getJWTToken();
    sub = String((parseJwt(token) as { sub?: unknown }).sub ?? '');
  } catch {
    sub = '';
  }
  const target = getImpersonation();
  return { owner: `${sub}|${target?.sub ?? ''}`, token, target };
}

export async function fetchProgressReportStudents(auth?: AuthSnapshot, query?: ListFetch): Promise<ProgressReportStudents> {
  const snapshot = auth ?? (await authSnapshot());
  if (snapshot.token === undefined) throw new ProgressReportStudentsError('no session token', 0);
  const response = await fetch(`${apiUrl}/reports/student-progress/students${schoolQuery(query)}`, {
    headers: authHeadersFor(snapshot.token, snapshot.target),
  });
  if (!response.ok) {
    throw new ProgressReportStudentsError(`progress-report students fetch failed: ${response.status}`, response.status);
  }
  const { roster: note, ...body } = (await response.json()) as ProgressReportStudents & { roster?: unknown };
  const roster = parseRosterMeta(note);
  return roster === null ? body : { ...body, roster };
}

let cached: { owner: string; key: string; promise: Promise<ProgressReportStudents> } | null = null;
const loaded = new Map<string, Promise<ProgressReportStudents>>();
const settled = new Map<string, ProgressReportStudents>();

function remember(key: string, promise: Promise<ProgressReportStudents>): void {
  loaded.set(key, promise);
  promise.then(
    (data) => {
      if (loaded.get(key) === promise) settled.set(key, data);
    },
    () => undefined,
  );
}

function loadedKey(owner: string, key: string): string {
  return `${owner}|${key}`;
}
let lastOwner: string | null = null;
const resetListeners = new Set<() => void>();

export function onProgressReportsReset(listener: () => void): () => void {
  resetListeners.add(listener);
  return () => {
    resetListeners.delete(listener);
  };
}

function notifyReset(): void {
  for (const listener of resetListeners) listener();
}

function noteOwner(owner: string): void {
  if (lastOwner !== null && lastOwner !== owner) {
    cached = null;
    loaded.clear();
    settled.clear();
    resetListState();
    notifyReset();
  }
  lastOwner = owner;
}

export async function currentIdentity(): Promise<string> {
  return (await authSnapshot()).owner;
}

const currentOwner = currentIdentity;

export type ProgressReportSession = { auth: AuthSnapshot; students: ProgressReportStudents | null };

async function cachedUnder(snapshot: AuthSnapshot, query: ListFetch | undefined): Promise<ProgressReportStudents> {
  const owner = snapshot.owner;
  const key = cacheKey(query);
  if (cached === null || cached.owner !== owner || cached.key !== key) {
    const held = loaded.get(loadedKey(owner, key));
    const promise =
      held ??
      fetchProgressReportStudents(snapshot, query).catch((error: unknown) => {
        if (cached?.promise === promise) cached = null;
        loaded.delete(loadedKey(owner, key));
        throw error;
      });
    remember(loadedKey(owner, key), promise);
    cached = { owner, key, promise };
  }
  return cached.promise;
}

// The list the page last loaded is the one the selection was made from, so a
// cold print asks for the school the saved selection names.
function savedSchoolQuery(): ListFetch {
  return { school: loadListState()?.query.school ?? null };
}

export async function cachedProgressReportStudents(attempts = 2): Promise<ProgressReportStudents> {
  return (await loadProgressReportSession(attempts)).students!;
}

// A selection may span the schools a session opened, so the print page gets
// the saved school's list with every other school this session has finished
// loading folded in; a school still in flight or failed contributes nothing
// and never holds the print page up.
function withOtherLoadedSchools(owner: string, data: ProgressReportStudents): ProgressReportStudents {
  const seen = new Set(data.students.map((s) => s.studentId));
  const students = [...data.students];
  for (const [key, other] of settled) {
    if (!key.startsWith(`${owner}|`) || other === data) continue;
    for (const student of other.students) {
      if (seen.has(student.studentId)) continue;
      seen.add(student.studentId);
      students.push(student);
    }
  }
  return students.length === data.students.length ? data : { ...data, students };
}

// The saved school's settled list when there is one, else any list this
// session has settled, so a print never waits on a request still in flight;
// only a cold session fetches.
async function sessionBase(snapshot: AuthSnapshot): Promise<ProgressReportStudents> {
  const owner = snapshot.owner;
  const saved = settled.get(loadedKey(owner, cacheKey(savedSchoolQuery())));
  if (saved) return saved;
  for (const [key, other] of settled) {
    if (key.startsWith(`${owner}|`)) return other;
  }
  return cachedUnder(snapshot, savedSchoolQuery());
}

export async function loadProgressReportSession(attempts = 2): Promise<ProgressReportSession> {
  const snapshot = await authSnapshot();
  noteOwner(snapshot.owner);
  const data = withOtherLoadedSchools(snapshot.owner, await sessionBase(snapshot));
  const after = await authSnapshot();
  noteOwner(after.owner);
  if (after.owner !== snapshot.owner) {
    if (attempts <= 1) throw new ProgressReportStudentsError('identity changed while the student list loaded', 0);
    return loadProgressReportSession(attempts - 1);
  }
  return { auth: snapshot, students: data };
}

export async function progressReportSessionOrNone(): Promise<ProgressReportSession> {
  try {
    return await loadProgressReportSession();
  } catch {
    const auth = await authSnapshot();
    noteOwner(auth.owner);
    return { auth, students: null };
  }
}

export async function loadProgressReportStudents(
  fetcher: (auth: AuthSnapshot, query: ListFetch) => Promise<ProgressReportStudents> = fetchProgressReportStudents,
  attempts = 2,
  query: ListFetch = { school: null },
): Promise<ProgressReportStudents> {
  const snapshot = await authSnapshot();
  const owner = snapshot.owner;
  noteOwner(owner);
  const key = loadedKey(owner, cacheKey(query));
  const data = await fetcher(snapshot, query);
  const after = await currentOwner();
  noteOwner(after);
  if (after !== owner) {
    if (attempts <= 1) throw new ProgressReportStudentsError('identity changed while the student list loaded', 0);
    return loadProgressReportStudents(fetcher, attempts - 1, query);
  }
  const promise = Promise.resolve(data);
  remember(key, promise);
  if (data.school !== undefined) remember(loadedKey(owner, cacheKey({ school: schoolLabel(data.school) })), promise);
  cached = { owner, key: cacheKey(query), promise };
  return data;
}

// A school the session already loaded answers again without asking; the
// list for it is as fresh as when it was first read, like the whole-district
// list was before.
export function rememberedSchools(
  fetcher: (auth: AuthSnapshot, query: ListFetch) => Promise<ProgressReportStudents>,
): (auth: AuthSnapshot, query: ListFetch) => Promise<ProgressReportStudents> {
  return (auth, query) => {
    const key = loadedKey(auth.owner, cacheKey(query));
    const held = loaded.get(key);
    if (held) return held;
    const promise = fetcher(auth, query).catch((error: unknown) => {
      if (loaded.get(key) === promise) loaded.delete(key);
      throw error;
    });
    remember(key, promise);
    return promise;
  };
}

export function resetProgressReportStudents(): void {
  cached = null;
  loaded.clear();
  settled.clear();
  lastOwner = null;
}

export function resetProgressReportsSession(): void {
  resetProgressReportStudents();
  resetListState();
  notifyReset();
}
